import { DatePipe, NgClass } from '@angular/common';
import { buildDeleteConfig } from '../../../../../utils/delete-confirmation';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AccordionModule } from 'primeng/accordion';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { DialogService } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ANALYSIS_POST_ALLOWED_PARAMETER_TYPES } from '../../../../../entity-definitions/analysis-post.definition';
import { measureFormulaDefinition } from '../../../../../entity-definitions/measure-formula.definition';
import { AUTOMATIC_ALLOWED_PARAMETER_TYPES } from '../../../../../entity-definitions/automatic-measure.definition';
import { EntityDialogService } from '../../../../../services/entity-dialog.service';
import { MeasureDetailService } from '../../../../../services/measure-detail.service';
import { ModalHelperService } from '../../../../../services/modal-helper.service';
import { TestModeService } from '../../../../../services/test-mode.service';
import { ConfirmationHelperService } from '../../../../../services/confirmation-helper.service';
import { ToastService } from '../../../../../services/toast.service';
import { EntityFormValue, isEntityFormDeleteAction } from '../../../../../types/entity-form-result.interface';
import { MeasureFormula } from '../../../../../types/measure-formula.interface';
import { PermissionScope } from '../../../../../types/permission-scope.enum';
import { FORMULA_HAS_VALUES, MEASURE_FORMULA_DATES_LOCKED } from '../../../../../utils/measure-error-codes';
import { CesButtonSecondaryComponent, CesDeleteButtonComponent } from '../../../../ui/ces-buttons';
import { CesIconComponent } from '../../../../ui/ces-icon/ces-icon.component';
import { CesProperty } from '../../../../ui/ces-property/ces-property.component';
import { FormulaExpressionComponent } from '../../../../ui/expression-display/expression-display.component';
import { ParameterMode } from '../../../../ui/inputs/parameters-input/parameter-types';
import { ParametersDisplayComponent } from '../../../../ui/parameters-display/parameters-display.component';
import { DetailedMeasureFormula } from '../../../../../services/api/generated/model';
import {
  isSuspendDialogDeleteResult,
  SuspendDialogData,
  SuspendDialogDeleteResult,
  SuspendMeasureDialogComponent,
} from './dialogs/suspend-measure-dialog.component';
import { extractServerErrorCode } from './formula-period';
import {
  computeFormulaStatuses,
  confirmOverwriteIfNeeded,
  findInForceFormula,
  findNextSegmentStartMs,
  formatShortLocalDate,
  FormulaStatus,
  isInactivePeriod,
} from './measure-formula.helpers';

@Component({
  selector: 'app-measure-formula',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AccordionModule,
    CardModule,
    CesButtonSecondaryComponent,
    CesDeleteButtonComponent,
    CesIconComponent,
    CesProperty,
    DatePipe,
    NgClass,
    DividerModule,
    DropdownModule,
    FormulaExpressionComponent,
    InputTextModule,
    MessageModule,
    ParametersDisplayComponent,
    RouterModule,
    TableModule,
    TextareaModule,
    ToastModule,
    TooltipModule,
    TranslocoModule,
  ],
  providers: [DialogService],
  templateUrl: './measure-formula.component.html',
})
export class MeasureFormulaComponent implements OnInit {
  protected readonly FRAGMENT_PREFIX = 'formula-';

  protected readonly ParameterMode = ParameterMode;
  protected readonly dataService = inject(MeasureDetailService);
  private readonly toastService = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly entityDialogService = inject(EntityDialogService);
  private readonly dialogService = inject(DialogService);
  private readonly modalHelper = inject(ModalHelperService);
  protected readonly t = inject(TestModeService);
  private readonly translateService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmationHelper = inject(ConfirmationHelperService);

  protected readonly isProcessing = signal(false);
  private readonly tick = signal(Date.now());

  protected readonly selectedFormulaId = signal<number | null>(null);

  protected readonly measure = this.dataService.measure;
  protected readonly measureFormulas = this.dataService.measureFormulas;
  protected readonly measureSortedFormulas = this.dataService.measureSortedFormulas;
  protected readonly units = this.dataService.units;

  protected readonly selectedFormula = computed(() => this.measureFormulas().find(f => f.id === this.selectedFormulaId()));

  protected readonly selectedFormulaNextStart = computed(() => {
    const selected = this.selectedFormula();
    if (!selected) return null;
    if (selected.endTs) return selected.endTs;
    const selectedMs = new Date(selected.startTs).getTime();
    const candidates = this.measureSortedFormulas().filter(f => f.id !== selected.id && new Date(f.startTs).getTime() > selectedMs);
    if (candidates.length === 0) return null;
    return candidates.reduce((earliest, f) => (new Date(f.startTs).getTime() < new Date(earliest.startTs).getTime() ? f : earliest))
      .startTs;
  });

  protected readonly selectedIsInactive = computed(() => {
    const selected = this.selectedFormula();
    return selected ? isInactivePeriod(selected) : false;
  });

  protected readonly writePermission = computed(() => ({
    permissionId: PermissionScope.Measures,
    type: 'write' as const,
    assetId: this.measure()?.assetId,
  }));

  protected readonly otherMeasures = computed(() => {
    const currentMeasure = this.measure();
    if (!currentMeasure) return [];
    return this.dataService.measures().filter(m => m.assetId === currentMeasure.assetId && m.id !== currentMeasure.id);
  });

  private readonly kindFormulaOptions = computed(() => {
    const kind = this.dataService.kind();
    if (kind === 'analysis-post') return { allowedParameterTypes: ANALYSIS_POST_ALLOWED_PARAMETER_TYPES };
    // CES-604 : fonctions historiques indisponibles pour les formules automatiques
    // (aucune cascade temporelle ne recalcule ces points).
    if (kind === 'automatic') {
      return { allowedParameterTypes: AUTOMATIC_ALLOWED_PARAMETER_TYPES, historicalFunctionsAvailable: false };
    }
    return {};
  });

  protected readonly formulaStatuses = computed<Map<number, FormulaStatus>>(() => {
    this.tick();
    return computeFormulaStatuses(this.measureSortedFormulas(), Date.now());
  });

  protected readonly formulaDateFormat = computed<Map<number, { from: string; to: string }>>(() => {
    const formulas = this.measureSortedFormulas();
    const startDayCount = new Map<string, number>();
    const endDayCount = new Map<string, number>();
    const dayKey = (iso: string | null | undefined): string | null => {
      if (!iso) return null;
      const d = new Date(iso);
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    };
    for (const f of formulas) {
      const s = dayKey(f.startTs);
      if (s) startDayCount.set(s, (startDayCount.get(s) ?? 0) + 1);
      const e = dayKey(f.endTs);
      if (e) endDayCount.set(e, (endDayCount.get(e) ?? 0) + 1);
    }
    const result = new Map<number, { from: string; to: string }>();
    for (const f of formulas) {
      const s = dayKey(f.startTs);
      const e = dayKey(f.endTs);
      result.set(f.id, {
        from: s && (startDayCount.get(s) ?? 0) > 1 ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy',
        to: e && (endDayCount.get(e) ?? 0) > 1 ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy',
      });
    }
    return result;
  });

  protected fromDateFormat(id: number): string {
    return this.formulaDateFormat().get(id)?.from ?? 'dd.MM.yyyy';
  }

  protected toDateFormat(id: number): string {
    return this.formulaDateFormat().get(id)?.to ?? 'dd.MM.yyyy';
  }

  protected readonly formulaClassesMap = computed<Map<number, Record<string, boolean>>>(() => {
    const statuses = this.formulaStatuses();
    const selectedId = this.selectedFormulaId();
    const map = new Map<number, Record<string, boolean>>();
    for (const [id, status] of statuses) {
      const selected = selectedId === id;
      const isGreen = status === 'active-since';
      const isOrange = status === 'suspended-since' || status === 'suspended-from';
      const isBlue = status === 'active-from';
      const isGrey = status === 'expired-active' || status === 'expired-suspended';
      map.set(id, {
        'border-l-transparent': !selected,
        'border-l-green-500': selected && isGreen,
        'border-l-orange-500': selected && isOrange,
        'border-l-blue-500': selected && isBlue,
        'border-l-gray-500': selected && isGrey,
      });
    }
    return map;
  });

  private readonly selectedTemporalState = computed<'current' | 'future' | 'past' | null>(() => {
    this.tick();
    const selected = this.selectedFormula();
    if (!selected) return null;
    const now = Date.now();
    const start = new Date(selected.startTs).getTime();
    const end = selected.endTs ? new Date(selected.endTs).getTime() : null;
    if (start > now) return 'future';
    if (end != null && end <= now) return 'past';
    return 'current';
  });

  protected readonly canSuspendNow = computed(() => {
    const selected = this.selectedFormula();
    return selected != null && !isInactivePeriod(selected);
  });

  protected readonly canReactivateNow = computed(() => {
    const selected = this.selectedFormula();
    if (!selected || !isInactivePeriod(selected)) return false;
    return this.selectedTemporalState() === 'current';
  });

  protected readonly resumeFormula = computed(() => {
    const selected = this.selectedFormula();
    if (!selected || !isInactivePeriod(selected)) return null;
    const selectedStartMs = new Date(selected.startTs).getTime();
    const predecessor = this.measureSortedFormulas()
      .filter(f => !isInactivePeriod(f) && new Date(f.startTs).getTime() < selectedStartMs)
      .sort((a, b) => new Date(b.startTs).getTime() - new Date(a.startTs).getTime())[0];
    return predecessor?.formula ?? null;
  });

  constructor() {
    const interval = setInterval(() => this.tick.set(Date.now()), 60_000);
    this.destroyRef.onDestroy(() => clearInterval(interval));
  }

  ngOnInit(): void {
    this.checkFragmentAndSelectFormula();

    this.route.fragment.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(fragment => {
      if (fragment?.startsWith(this.FRAGMENT_PREFIX)) {
        const id = Number(fragment.replace(this.FRAGMENT_PREFIX, ''));
        if (!isNaN(id) && this.measureFormulas().some(f => f.id === id)) {
          this.selectedFormulaId.set(id);
        }
      } else if (!this.selectedFormulaId()) {
        const first = this.measureSortedFormulas()[0];
        if (first) this.selectFormula(first.id);
      }
    });
  }

  private checkFragmentAndSelectFormula(): void {
    const fragment = this.route.snapshot.fragment;
    if (fragment?.startsWith(this.FRAGMENT_PREFIX)) {
      const id = Number(fragment.replace(this.FRAGMENT_PREFIX, ''));
      if (!isNaN(id) && this.measureFormulas().some(f => f.id === id)) {
        this.selectedFormulaId.set(id);
        return;
      }
    }
    const target = findInForceFormula(this.measureSortedFormulas(), Date.now()) ?? this.measureSortedFormulas()[0];
    if (target) this.selectFormula(target.id);
  }

  protected selectFormula(id: number): void {
    this.selectedFormulaId.set(id);
    void this.router.navigate([], {
      fragment: this.FRAGMENT_PREFIX.concat(String(id)),
      relativeTo: this.route,
      replaceUrl: true,
    });
  }

  protected deleteConfirmationConfig(formula: MeasureFormula) {
    return buildDeleteConfig('entities.measureFormula.messages.deleteConfirm', formula.name);
  }

  private handleFormulaSaveError(error: unknown): void {
    if (extractServerErrorCode(error) === MEASURE_FORMULA_DATES_LOCKED) {
      this.toastService.error('entities.measureFormula.messages.datesLocked');
      return;
    }
    this.toastService.error('entities.measureFormula.messages.updateError');
  }

  protected deleteFormula(entity: MeasureFormula): void {
    if (!entity) return;

    const measure = this.measure();
    this.dataService
      .deleteMeasureFormula(entity.id)
      .then(async () => {
        if (measure) {
          this.dataService.invalidateMeasureFormulaCache(measure.id);
          await this.dataService.getMeasureFormulaByMeasureId(measure.id);
        }
        if (this.selectedFormulaId() === entity?.id) {
          const next = this.measureSortedFormulas()[0];
          if (next) {
            this.selectFormula(next.id);
          } else {
            this.selectedFormulaId.set(null);
            void this.router.navigate([], { relativeTo: this.route });
          }
        }
      })
      .catch((error: unknown) => {
        if (extractServerErrorCode(error) === FORMULA_HAS_VALUES) {
          this.toastService.error('entities.measureFormula.messages.deleteBlockedHasValues');
          return;
        }
        const message = error instanceof Error ? error.message : this.translateService.translate('common.toasts.unknownError');
        this.toastService.error('common.toasts.deleteFailed', undefined, undefined, undefined, { error: message });
      });
  }

  protected createFormula(): void {
    const measure = this.measure();
    if (!measure) return;

    this.entityDialogService
      .openCreateDialog<MeasureFormula>({
        header: this.translateService.translate('entities.measureFormula.messages.createTitle'),
        entityDefinition: measureFormulaDefinition,
        mode: 'create',
        size: 'large',
        additionalData: {
          measureId: measure.id,
          measures: this.otherMeasures(),
          units: this.units(),
          selectedMeasureFormula: this.selectedFormula(),
          formulas: this.measureFormulas(),
          ...this.kindFormulaOptions(),
        },
      })
      .subscribe(formData => {
        if (!formData || isEntityFormDeleteAction(formData)) return;
        void this.submitCreateFormula(measure.id, formData);
      });
  }

  private async submitCreateFormula(measureId: number, formData: EntityFormValue<MeasureFormula>): Promise<void> {
    if (!formData.startTs) return;
    const newStartMs = new Date(formData.startTs).getTime();
    const segments = this.measureSortedFormulas();
    // A formula created without an explicit end no longer paints to infinity: it stops at the
    // next surviving segment (formula or suspension), so the preview must reflect the same bound
    // the API now enforces rather than flagging later segments as swallowed.
    const newEndMs = findNextSegmentStartMs(segments, newStartMs);
    const proceed = await confirmOverwriteIfNeeded(
      {
        ask: (messageKey, headerKey, params, icon, acceptLabelKey) =>
          this.confirmationHelper.ask(messageKey, headerKey, params, icon, acceptLabelKey),
        translate: (key, params) => this.translateService.translate(key, params),
      },
      segments,
      newStartMs,
      newEndMs,
    );
    if (!proceed) return;

    try {
      const created = await this.dataService.createMeasureFormulaForMeasure(measureId, formData);
      await this.dataService.getMeasureFormulaByMeasureId(measureId);
      if (created?.id) this.selectFormula(created.id);
    } catch {
      this.toastService.error('entities.measureFormula.messages.createError');
    }
  }

  protected editFormula(formula: MeasureFormula): void {
    const measure = this.measure();
    if (!measure) return;

    this.entityDialogService
      .openEditDialog<MeasureFormula>(formula, {
        header: this.translateService.translate('entities.measureFormula.messages.editTitle', {
          date: formatShortLocalDate(formula.startTs),
        }),
        entityDefinition: measureFormulaDefinition,
        mode: 'edit',
        size: 'large',
        additionalData: {
          measureId: measure.id,
          units: this.units(),
          measures: this.otherMeasures(),
          formulas: this.measureFormulas(),
          ...this.kindFormulaOptions(),
        },
        onDelete: entity => this.deleteFormula(entity),
      })
      .subscribe(result => {
        if (!result) return;
        if (isEntityFormDeleteAction(result)) {
          this.deleteFormula(result.entity);
          return;
        }
        void (async () => {
          try {
            const formulaId = result['id'];
            if (formulaId === undefined) return;

            const updated = await this.dataService.updateMeasureFormula(formulaId, result);
            this.dataService.invalidateMeasureFormulaCache(measure.id);
            await this.dataService.getMeasureFormulaByMeasureId(measure.id);
            this.tick.set(Date.now());
            // An edit can merge the segment into an identical neighbor server-side, so the
            // surviving id (potentially different from the one being edited) is the only reliable
            // selection target; fall back to the first segment if the response carries none.
            if (updated?.id) {
              this.selectFormula(updated.id);
            } else {
              const next = this.measureSortedFormulas()[0];
              if (next) this.selectFormula(next.id);
            }
          } catch (error) {
            this.handleFormulaSaveError(error);
          }
        })();
      });
  }

  protected confirmSuspend(): void {
    if (this.isProcessing()) return;
    if (!this.canSuspendNow()) return;
    const measure = this.measure();
    const selected = this.selectedFormula();
    if (!measure || !selected) return;

    // Suspensions schedule as freely as formulas: adjoining segments merge server-side, so the
    // button always opens a fresh creation. Editing an existing suspension stays reachable through
    // the timeline segment itself (editInactivePeriod).
    this.openSuspendDialog({
      mode: 'create',
      measureId: measure.id,
      segments: this.measureSortedFormulas(),
    });
  }

  protected editInactivePeriod(): void {
    if (this.isProcessing()) return;
    const selected = this.selectedFormula();
    const measure = this.measure();
    if (!selected || !measure || !this.selectedIsInactive()) return;

    this.openSuspendDialog({
      mode: 'edit',
      measureId: measure.id,
      inactiveId: selected.id,
      startTs: selected.startTs,
      endTs: selected.endTs ?? null,
      comment: selected.suspendComment ?? null,
      segments: this.measureSortedFormulas(),
    });
  }

  protected async confirmReactivate(): Promise<void> {
    if (this.isProcessing()) return;
    const confirmed = await this.confirmationHelper.ask(
      'entities.measureFormula.messages.confirmReactivate',
      'entities.measureFormula.messages.reactivateTitle',
      undefined,
      'pi pi-exclamation-circle',
      'entities.measureFormula.messages.reactivate',
    );
    if (confirmed) void this.executeReactivate();
  }

  private openSuspendDialog(data: SuspendDialogData): void {
    const headerKey =
      data.mode === 'edit' ? 'entities.measureFormula.messages.editInactiveTitle' : 'entities.measureFormula.messages.suspendTitle';
    const ref = this.dialogService.open(
      SuspendMeasureDialogComponent,
      this.modalHelper.getSmallConfig({
        focusOnShow: false,
        header: this.translateService.translate(headerKey),
        data,
      }),
    );

    // The dialog closes with the repainted segment for create/edit, or with the delete marker
    // once the in-dialog delete action fired; the create/edit toast is routed by the mode the
    // dialog was opened with (an edit is a delete-and-reinsert server-side, so its own result
    // never carries a marker), while the delete marker always routes to the deletion toast.
    ref.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: DetailedMeasureFormula | SuspendDialogDeleteResult | undefined) => {
        if (!result) return;
        if (isSuspendDialogDeleteResult(result)) {
          void this.onSuspendDialogDeleteSuccess();
          return;
        }
        const toastKey =
          data.mode === 'edit'
            ? 'entities.measureFormula.messages.editInactiveSuccess'
            : 'entities.measureFormula.messages.suspendSuccess';
        void this.onSuspendDialogSuccess(result, toastKey);
      });
  }

  private async onSuspendDialogSuccess(response: DetailedMeasureFormula, toastKey: string): Promise<void> {
    const measure = this.measure();
    if (!measure) return;
    this.dataService.invalidateMeasureFormulaCache(measure.id);
    await this.dataService.getMeasureFormulaByMeasureId(measure.id);
    this.tick.set(Date.now());
    this.toastService.success(toastKey);
    // The repaint replaces the edited row, so the response id is the only valid selection target.
    if (response.id) {
      this.selectFormula(response.id);
    }
  }

  private async onSuspendDialogDeleteSuccess(): Promise<void> {
    const measure = this.measure();
    if (!measure) return;
    this.dataService.invalidateMeasureFormulaCache(measure.id);
    await this.dataService.getMeasureFormulaByMeasureId(measure.id);
    this.tick.set(Date.now());
    this.toastService.success('entities.measureFormula.messages.deleteSuspensionSuccess');
    const next = this.measureSortedFormulas()[0];
    if (next) this.selectFormula(next.id);
    else this.selectedFormulaId.set(null);
  }

  private async executeReactivate(): Promise<void> {
    const measure = this.measure();
    if (!measure) return;

    this.isProcessing.set(true);
    try {
      const created = await this.dataService.reactivateMeasure(measure.id);
      this.dataService.invalidateMeasureFormulaCache(measure.id);
      await this.dataService.getMeasureFormulaByMeasureId(measure.id);
      this.tick.set(Date.now());
      this.toastService.success('entities.measureFormula.messages.reactivateSuccess');
      if (created?.id) this.selectFormula(created.id);
    } catch {
      this.toastService.error('entities.measureFormula.messages.reactivateError');
    } finally {
      this.isProcessing.set(false);
    }
  }
}
